import { useState, useCallback, useEffect } from 'react';
import { getValidToken } from '../../../services/tokenRefreshService';
import config from '../../../config';
import type {
  MissionTemplate,
  MissionInstance,
  DashboardStats,
} from '../types';

const API_BASE = `${config.API_URL}/admin/missions`;

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getValidToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || res.statusText);
  }
  return res.json();
}

// ─── Template CRUD ──────────────────────────────────────────────────────────

export function useMissionTemplates() {
  const [data, setData] = useState<MissionTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (filters: Record<string, any> = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v != null) params.set(k, String(v)); });
      const result = await apiFetch<{ data: MissionTemplate[]; total: number }>(`?${params}`);
      setData(result.data);
      setTotal(result.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, total, loading, error, refresh: fetch };
}

export async function createTemplate(body: Partial<MissionTemplate>): Promise<{ success: boolean; mission: MissionTemplate }> {
  return apiFetch('', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateTemplate(id: string, body: Partial<MissionTemplate>): Promise<{ success: boolean; mission: MissionTemplate }> {
  return apiFetch(`/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiFetch(`/${id}`, { method: 'DELETE' });
}

export async function activateTemplate(id: string): Promise<void> {
  await apiFetch(`/${id}/activate`, { method: 'POST' });
}

export async function pauseTemplate(id: string): Promise<void> {
  await apiFetch(`/${id}/pause`, { method: 'POST' });
}

export async function triggerMission(id: string, body: { assignedTo: string; storeId?: string; expiresInHours?: number }): Promise<{ success: boolean; instance: MissionInstance }> {
  return apiFetch(`/${id}/trigger`, { method: 'POST', body: JSON.stringify(body) });
}

// ─── Instances ──────────────────────────────────────────────────────────────

export function useMissionInstances(missionId: string) {
  const [data, setData] = useState<MissionInstance[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (filters: Record<string, any> = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v != null) params.set(k, String(v)); });
      const result = await apiFetch<{ data: MissionInstance[]; total: number }>(`/${missionId}/instances?${params}`);
      setData(result.data);
      setTotal(result.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, total, loading, error, refresh: fetch };
}

// ─── Dashboard Stats ────────────────────────────────────────────────────────

export function useMissionDashboard(storeId?: string) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = storeId ? `?storeId=${storeId}` : '';
      const result = await apiFetch<DashboardStats>(`/dashboard${params}`);
      setStats(result);
    } catch {
      // Silently fail for dashboard stats
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { stats, loading, refresh: fetch };
}
